'use client';
import { Z_INDEX } from '@/constants';
import { TestCourseGroupTypeEnum } from '@/services/types/license-test-course.type';
import { useTestCourseModalStore } from '@/store/test-course-modal.store';
import { X } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import ImageOptimizer from './image-optimizer';
import Divider from './divider';

export default function TestCourseModal() {
  const { close, selectedGroup, selectedTestCourse } =
    useTestCourseModalStore();

  // 모달 닫기 함수 (history 정리 포함)
  const handleClose = useCallback(() => {
    // history에서 모달 상태 제거
    if (window.history.state?.modal) {
      window.history.back();
    }
    close();
  }, [close]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      handleClose();
    };

    // 모달이 열릴 때 history에 상태 추가
    window.history.pushState({ modal: true }, '');

    // popstate 이벤트 리스너 추가
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleClose]);

  return (
    <div
      className="desktop:items-center desktop:justify-center fixed inset-0 top-0 flex items-stretch overflow-y-auto"
      style={{
        zIndex: Z_INDEX.Modal,
      }}
    >
      {/* Backdrop - only visible on desktop */}
      {/* <div className="absolute inset-0 block bg-black/40 backdrop-blur-sm" /> */}
      <div
        className="absolute inset-0 block bg-black/40"
        onClick={handleClose}
      />

      <div className="desktop:max-w-[1020px] desktop:overflow-hidden desktop:rounded-2xl desktop:shadow-lg desktop:h-auto desktop:max-h-[calc(100vh-80px)] relative flex h-full w-full flex-col bg-white">
        <div
          className="border-stroke-white desktop:pt-8 desktop:pb-6 desktop:px-10 sticky top-0 z-10 flex items-center justify-between border-b-1 bg-white px-6 py-5"
          style={{
            zIndex: Z_INDEX.Modal,
          }}
        >
          <h2 className="desktop:typo-32 typo-20 text-level-2 flex items-center gap-2 font-extrabold">
            {selectedGroup === TestCourseGroupTypeEnum.SKILL
              ? '장내코스'
              : '도로코스'}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="6"
              height="6"
              viewBox="0 0 6 6"
              fill="none"
            >
              <circle cx="3" cy="3" r="3" fill="#DDDDDD" />
            </svg>
            <div className="desktop:typo-32 typo-20 text-level-4 desktop:font-bold font-extrabold">
              {selectedTestCourse?.title}
            </div>
          </h2>
          <button className="text-black" onClick={handleClose}>
            <X
              size={28}
              strokeWidth={1.5}
              className="desktop:hidden cursor-pointer"
            />
            <X
              size={32}
              strokeWidth={2}
              className="desktop:block hidden cursor-pointer"
            />
          </button>
        </div>
        <div className="desktop:p-10 overflow-y-auto p-7">
          {selectedTestCourse?.youtubeVideoId && (
            <>
              <div className="desktop:typo-24 typo-18 text-level-3 desktop:leading-relaxed mb-4 flex items-center gap-2 leading-normal font-semibold">
                <ImageOptimizer
                  src={
                    '/images/exam/license-test-course/icon_test_course_video.svg'
                  }
                  alt={selectedTestCourse?.title || ''}
                  width={24}
                  height={24}
                />
                {`${selectedTestCourse?.title} 코스영상`}
              </div>
              <div className="relative aspect-video w-full">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedTestCourse?.youtubeVideoId}`}
                  title={selectedTestCourse?.title + ' 유튜브 영상'}
                  className="h-full w-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              {selectedTestCourse?.imagePath && <Divider className="my-7" />}
            </>
          )}
          {selectedTestCourse?.imagePath && (
            <>
              <div className="desktop:typo-24 typo-18 text-level-3 desktop:leading-relaxed mb-4 flex items-center gap-2 leading-normal font-semibold">
                <ImageOptimizer
                  src={
                    '/images/exam/license-test-course/icon_test_course_image.svg'
                  }
                  alt={selectedTestCourse?.title || ''}
                  width={24}
                  height={24}
                />
                {`${selectedTestCourse?.title} 코스약도`}
              </div>
              <div className="relative aspect-video w-full">
                <ImageOptimizer
                  src={selectedTestCourse?.imagePath}
                  alt={selectedTestCourse?.title || ''}
                  fill
                  className="object-contain object-center"
                />
              </div>
            </>
          )}

          {(selectedTestCourse?.youtubeVideoId ||
            selectedTestCourse?.imagePath) &&
            selectedTestCourse?.content && (
              <>
                <div className="desktop:typo-20 typo-15 text-level-3 desktop:leading-relaxed pt-7 leading-normal font-medium">
                  {selectedTestCourse?.content}
                </div>
              </>
            )}
        </div>
      </div>
    </div>
  );
}
